/**
 * Utility functions for R1C1 coordinate system
 * R1C1 format: "R{row}C{col}" where row and col are 1-based
 */

export interface R1C1Position {
  row: number;
  col: number;
}

/**
 * Convert R1C1 string to position object
 * @param r1c1 - R1C1 string like "R1C1", "R2C3"
 * @returns R1C1Position object with 1-based row and col
 */
export function parseR1C1(r1c1: string): R1C1Position {
  const match = r1c1.match(/^R(\d+)C(\d+)$/);
  if (!match) {
    throw new Error(`Invalid R1C1 format: ${r1c1}`);
  }
  return {
    row: parseInt(match[1], 10),
    col: parseInt(match[2], 10)
  };
}

/**
 * Convert position object to R1C1 string
 * @param position - R1C1Position object with 1-based row and col
 * @returns R1C1 string like "R1C1", "R2C3"
 */
export function formatR1C1(position: R1C1Position): string {
  return `R${position.row}C${position.col}`;
}

/**
 * Convert R1C1 to 0-based array index for a given grid size
 * @param r1c1 - R1C1 string like "R1C1", "R2C3"
 * @param columns - Number of columns in the grid
 * @returns 0-based array index
 */
export function r1c1ToIndex(r1c1: string, columns: number): number {
  const pos = parseR1C1(r1c1);
  return (pos.row - 1) * columns + (pos.col - 1);
}

/**
 * Convert 0-based array index to R1C1 for a given grid size
 * @param index - 0-based array index
 * @param columns - Number of columns in the grid
 * @returns R1C1 string like "R1C1", "R2C3"
 */
export function indexToR1C1(index: number, columns: number): string {
  const row = Math.floor(index / columns) + 1;
  const col = (index % columns) + 1;
  return formatR1C1({ row, col });
}

/**
 * Convert R1C1 position to rotated R1C1 position for landscape mode
 * @param r1c1 - R1C1 string like "R1C1", "R2C3"
 * @param originalRows - Number of rows in portrait mode
 * @param originalColumns - Number of columns in portrait mode
 * @returns Rotated R1C1 string for landscape mode
 */
export function rotateR1C1ToLandscape(r1c1: string, originalRows: number, originalColumns: number): string {
  // The buttons keep their exact same R1C1 coordinates
  // Only the visual interpretation of those coordinates changes due to axis rotation
  // Portrait: R1C1 = top-left, R5C3 = bottom-right
  // Landscape: R1C1 = bottom-left, R5C3 = top-right
  return r1c1;
}

/**
 * Convert R1C1 position from landscape back to portrait mode
 * @param r1c1 - R1C1 string in landscape mode
 * @param originalRows - Number of rows in portrait mode
 * @param originalColumns - Number of columns in portrait mode
 * @returns R1C1 string for portrait mode
 */
export function rotateR1C1FromLandscape(r1c1: string, originalRows: number, originalColumns: number): string {
  // The buttons keep their exact same R1C1 coordinates
  // Only the visual interpretation of those coordinates changes due to axis rotation
  // Portrait: R1C1 = top-left, R5C3 = bottom-right
  // Landscape: R1C1 = bottom-left, R5C3 = top-right
  return r1c1;
}

/**
 * Get all R1C1 positions for a grid
 * @param rows - Number of rows
 * @param columns - Number of columns
 * @returns Array of R1C1 strings in row-major order
 */
export function getAllR1C1Positions(rows: number, columns: number): string[] {
  const positions: string[] = [];
  for (let row = 1; row <= rows; row++) {
    for (let col = 1; col <= columns; col++) {
      positions.push(formatR1C1({ row, col }));
    }
  }
  return positions;
}

/**
 * Check if two R1C1 positions are equal
 * @param pos1 - First R1C1 string
 * @param pos2 - Second R1C1 string
 * @returns True if positions are equal
 */
export function r1c1Equal(pos1: string, pos2: string): boolean {
  return pos1 === pos2;
}

/**
 * Get the effective grid dimensions for landscape mode
 * @param originalRows - Number of rows in portrait mode
 * @param originalColumns - Number of columns in portrait mode
 * @param isLandscape - Whether in landscape mode
 * @returns Object with effective rows and columns
 */
export function getEffectiveDimensions(originalRows: number, originalColumns: number, isLandscape: boolean) {
  return {
    rows: isLandscape ? originalColumns : originalRows,
    columns: isLandscape ? originalRows : originalColumns
  };
}
